import { Component, inject, OnInit } from '@angular/core';
import { MatDialogConfig } from '@angular/material/dialog';
import { fadeInAnimation } from 'app/animations';
import { FilterValues } from 'app/core/interfaces/filtervalues.interface';
import { OrderSandbox } from 'app/core/sandboxes/order.sandbox';
import { RecurrenceService } from 'app/core/services/recurrence.service';
import { ScrollService } from 'app/core/services/scroll.service';
import { CDIncoterm, CDOpeningHours, CDTo, ContractRestrictions } from 'app/core/store/contract-details/contract-details.interface';
import { ContractInfo } from 'app/core/store/contract-info/contract-info.interface';
import { OrderDetail, OrderMaterial, } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { EmailActors, EmailActorsItem, SimplifiedEmailActor } from 'app/core/store/email-actors/email-actors.interface';
import * as orderActions from 'app/core/store/order-detail/order-detail.actions';
import { goAction } from 'app/core/store/router/router.actions';
import { SessionState } from 'app/core/store/session/session.interface';
import { Customer } from 'app/core/store/session/user-context/user-context.interface';
import { TemplateState } from 'app/core/store/template/template.interface';
import { getFirst$$, util } from 'app/core/util/util';
import { FlorderEditContainer } from 'app/modules/florder/florder-edit-container/florder-edit.container';
import { OrderPanelStatuses } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { SelectEmailAddressesModalComponent } from 'app/shared/modal/modal-select-email-addresses.component';
import { of } from 'rxjs';
import { combineLatest, distinctUntilChanged, filter, first, map, takeWhile } from 'rxjs/operators';
import { RootState } from '../../../core/store';

@Component({
  selector: 'app-order-edit',
  templateUrl: './order-edit.container.html',
  styleUrls: ['../../florder/florder-edit-container/florder-edit.container.scss'],
  animations: [fadeInAnimation],
})
export class OrderEditContainer extends FlorderEditContainer<OrderMaterial> implements OnInit {
  orderDetail: OrderDetail;
  loadedEpp: number;
  maxEpp: number;
  restrictions: ContractRestrictions;
  emailAddresses: EmailActorsItem[];
  contractInfo: ContractInfo;
  customers: Customer[] = null;
  isSuperUser: boolean;
  busyProcess$ = this.store.select((state: RootState) => state.ui.busyProcesses).pipe(
    map(busyProcesses => busyProcesses?.length ? busyProcesses[0] : undefined)
  );

  public sandbox = inject(OrderSandbox);
  private scrollService = inject(ScrollService);
  private recurrenceService = inject(RecurrenceService);

  availablePickingDates: {
    full: string[];
    nonFull: string[];
  };
  private loadingDates: string[];
  filterValues: FilterValues;

  constructor() {
    super('orders');

    // Contract
    const contractInfo$ = this.store.pipe(
      map(state => state.contractInfo),
      takeWhile(() => !this.destroyed),
      distinctUntilChanged()
    );

    this.store
      .select('template')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((templateState: TemplateState) => (this.templateState = templateState));

    this.store
      .pipe(
        map(state => state.orders.filterValues),
        takeWhile(() => !this.destroyed),
        distinctUntilChanged()
      )
      .subscribe(filterValues => (this.filterValues = filterValues));

    this.store
      .pipe(
        map(state => state.editOrderDetail),
        combineLatest(contractInfo$, (editOrderDetail, contractInfo) => {
          return {
            contractInfo,
            editOrderDetail,
          };
        }),
        takeWhile(() => !this.destroyed),
        filter(({ editOrderDetail, contractInfo }) => !!editOrderDetail.orderDetail && !contractInfo.loading),
        distinctUntilChanged()
      )
      .subscribe(({ editOrderDetail, contractInfo }) => {
        this.loadedEpp = editOrderDetail.loadedEpp;
        this.maxEpp = editOrderDetail.maxEpp;
        this.availablePickingDates = editOrderDetail.availablePickingDates;
        this.mode = editOrderDetail.mode;
        const materialEdit = !!(editOrderDetail.orderDetail && editOrderDetail.orderDetail.materials && editOrderDetail.mode.material);
        const materialExists = materialEdit && editOrderDetail.orderDetail.materials.find(m => m.internalId === editOrderDetail.mode.material.internalId) != null;
        this.panelStatus = this.getPanelStatusses(materialEdit, materialExists);
        this.orderDetail = editOrderDetail.orderDetail;

        this.contractInfo = util.jsonDeepCopy(contractInfo); // must be modifiable
        let orderType = this.contractInfo.orderTypes.find(o => o.id === this.orderDetail.deliveryMethod.type);
        let shippingCondition =
          orderType && this.orderDetail.deliveryMethod.shippingCondition && orderType.shippingConditions.find(sc => sc.id === this.orderDetail.deliveryMethod.shippingCondition.id);
        const from = shippingCondition && shippingCondition.froms.find(from => from.id === this.orderDetail.deliveryMethod.from.id);
        const froms = shippingCondition && (this.orderDetail.deliveryMethod.from == null ? shippingCondition.froms : from ? [from] : []);
        const to =
          froms &&
          this.orderDetail.deliveryMethod.to &&
          froms.reduce((to: CDTo, from) => to || (from && from.tos.find(to => to.id === this.orderDetail.deliveryMethod.to.id)), null);
        const tos = froms && this.orderDetail.deliveryMethod.to == null ? from.tos : to ? [to] : []; // if to selected, tos contains only selected to
        if (this.orderDetail.deliveryMethod.incoterm) {
          this.incoterm = tos && tos.reduce((incoterm: CDIncoterm, to) => incoterm || (to && to.incoterms.find(it => it.id === this.orderDetail.deliveryMethod.incoterm.id)), null);
        } else {
          this.incoterm = tos && tos.reduce((incoterm: CDIncoterm, to) => incoterm || (to && to.incoterms.length > 0 && to.incoterms[0]), null);
        }

        // If view mode and contract doesn't contain orderType, from or to, add it to enable readonly-view
        if (this.mode.state === 'view' && this.orderDetail.deliveryMethod.from !== null && this.orderDetail.deliveryMethod.to !== null) {
          if (!orderType) {
            orderType = {
              id: this.orderDetail.deliveryMethod.type,
              shippingConditions: [
                {
                  id: '', // TODO test this
                  name: '',
                  froms: [],
                },
              ],
            };
            this.contractInfo.orderTypes.push(orderType);
          }
          if (shippingCondition == null) {
            shippingCondition = {
              id: this.orderDetail.deliveryMethod.shippingCondition.id,
              name: this.orderDetail.deliveryMethod.shippingCondition.name,
              froms: [],
            };
            orderType.shippingConditions.push(shippingCondition);
          }
          orderType.shippingConditions.reverse();
          if (!froms || froms.length === 0) {
            shippingCondition.froms.push({
              id: this.orderDetail.deliveryMethod.from.id,
              isDefault: false,
              hidden: false,
              openingHours: [],
              tos: [],
            });
          }
          shippingCondition = orderType.shippingConditions.find(sc => sc.id === this.orderDetail.deliveryMethod.shippingCondition.id);
          const from = shippingCondition.froms.find(f => f.id === this.orderDetail.deliveryMethod.from.id);
          const to = from.tos.find(t => t.id === this.orderDetail.deliveryMethod.to.id);
          const incoterm = to && to.incoterms.find(ic => ic.id === this.orderDetail.deliveryMethod.shippingCondition.id);
          if (!incoterm) {
            this.incoterm = {
              id: '',
              name: '',
              isDefault: true,
              combination: null,
              packings: [],
              pallets: [],
            };
          }
        }

        if (editOrderDetail.mode.state === 'recurrence-dates' && !this.recurrenceService.isOpen() && editOrderDetail.recurrenceDates) {
          this.recurrenceService.openRecurrenceDatesDialog({
            statePath: 'editOrderDetail',
            cb: this.proceed.bind(this),
            goAction: (order: OrderDetail) =>
              goAction({
                path: [`/orders/${order.etmOrderNumber}`],
                query: { salesOrderNumber: order.salesOrderNumber, back: this.backUrl },
              }),
            backToConfirmAction: new orderActions.OrderDetailBackToCreateConfirmAction(),
            createButtonLabel: 'ORDERS.RECURRENCE.CREATE',
            gotoOverviewUrl: '/orders',
          });
        }
      });

    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.customerInfo),
        distinctUntilChanged()
      )
      .subscribe(customerInfo => {
        this.restrictions = customerInfo.restrictions;
      });

    this.store
      .select('emailActors')
      .pipe(takeWhile(() => !this.destroyed))
      .subscribe((emailActors: EmailActors) => {
        this.emailAddresses = emailActors.items.filter(it => it.isActive);
      });

    this.store
      .pipe(
        takeWhile(() => !this.destroyed),
        map(state => state.session),
        distinctUntilChanged()
      )
      .subscribe((session: SessionState) => {
        this.isSuperUser = session.userContext.isSuperUser;
        this.customers = session.customerPermissions.data.createOrUpdateOrder;
      });
  }

  ngOnInit(): void {
    this.scrollService.scrollToElement('order-edit-top');
  }

  protected getPanelStatusses(materialEdit: boolean, materialExists: boolean): OrderPanelStatuses {
    const panelStatuses = super.getPanelStatusses(materialEdit, materialExists);
    return {
      ...panelStatuses,
      planning: {
        ...panelStatuses.planning,
        showRecurrence: this.mode.state !== 'view' && !panelStatuses.planning.createTemplate && !panelStatuses.planning.editTemplate,
      },
    };
  }

  editCustomerClicked() {
    this.store.dispatch(new orderActions.OrderDetailEditCustomerAction());
  }

  editDeliveryMethodClicked() {
    this.store.dispatch(new orderActions.OrderDetailEditDeliveryMethodAction());
  }

  editDetailClicked() {
    this.store.dispatch(new orderActions.OrderDetailCreateEditOrderDetailAction());
  }

  editPlanningClicked(): void {
    this.store.dispatch(new orderActions.OrderDetailCreateEditOrderPlanningAction());
  }

  setCustomer(form): void {
    // if customer is the same, so no new one set => don't load customerInfo again (and clear form)
    if (this.orderDetail.customer && this.orderDetail.customer.id === form.customer.id) {
      this.store.dispatch(new orderActions.OrderDetailCreateSetCustomerAction({ customer: form.customer }));
    } else {
      this.sandbox
        .loadCustomerInfo(form.customer, true)
        .pipe(first())
        .subscribe(() => {
          this.store.dispatch(new orderActions.OrderDetailCreateSetCustomerAction({ customer: form.customer }));
        });
    }
  }

  setDeliveryMethod(form): void {
    const orderTypeId = form.type;
    const shippingCondition = form.shippingCondition.id;
    const fromId = form.from.id;
    const toId = form.to.id;
    const incoterm = form.incoterm.id;

    if (!orderTypeId) {
      this.azureMonitoringService?.logEvent(`OrderEditContainer.setDeliveryMethod(): empty orderTypeId '${form.type}'`);
      return;
    }

    this.sandbox.loadContractInfoMaterials({ orderTypeId, shippingCondition, fromId, toId, incoterm }).subscribe(
      () => {
        this.route.queryParamMap.pipe(first()).subscribe(paramMap => {
          this.store.dispatch(
            new orderActions.OrderDetailCreateSetDeliveryMethodAction({
              deliveryMethod: form,
              loadingDate: paramMap.get('date'),
            })
          );
          if (this.mode.state === 'create') {
            this.scrollService.scrollToElement('step-details');
          }
        });
      },
      error => {
        this.sandbox.showWarning('ERRORS.CONTRACT-INFO.TITLE', 'ERRORS.CONTRACT-INFO.INVALID_CONTRACT_PART_1');
      }
    );
  }

  removeMaterial(material): void {
    this.sandbox.removeMaterial(material);
  }

  editMaterial(material) {
    this.store.dispatch(new orderActions.OrderDetailCreateStartEditMaterialAction(material));
  }

  getMaterials(): (OrderMaterial)[] {
    return this.orderDetail.materials;
  }

  resetMaterialsAndPlanning() {
    this.store.dispatch(new orderActions.OrderDetailResetMaterialsAndPlanningAction());
  }

  saveMaterial(material) {
    if (this.checkMaterialIsUnique(material)) {
      this.store.dispatch(
        new orderActions.OrderDetailCreateSaveMaterialStartAction({
          material,
          preventRestCall: false,
          showPopup: true,
        })
      );
    }
  }

  create(data) {
    this.proceed();
  }

  submitClicked() {
    this.proceed();
  }

  /**
   * Method to finalize creation of an order or template.  Now dialog windows can appear if needed, or the order / template is created immediately.
   *
   * possible outcomes:
   * - Is a template ==> create template
   * - Is a 'create order' with recurrence on and loadingDate not yet an array ==> open recurrence dialog
   * - Is a 'create order' with recurrence on, with emailAddresses list ==> open recurrence dialog
   * - Is a 'create order' without recurrence but customer has emailAddresses list ==> Open email dialog
   * - Is a 'create order' without recurrence, without emailAddresses list ==> Create order
   */
  async proceed(loadingDates?: string[]) {
    this.loadingDates = loadingDates;
    if (this.mode.createTemplate || this.mode.editTemplate) {
      this.sandbox.submitCreateTemplate(this.mode.createTemplate);
    } else {
      // Check for recurrence without array of dates
      if (this.orderDetail.planning.recurrence && this.orderDetail.planning.recurrence.active && !this.loadingDates) {
        this.store.dispatch(new orderActions.OrderDetailGetRecurrenceDatesAction(this.orderDetail));
      } else if (this.emailAddresses.length > 0 && this.userContext.useEmailActors) {
        // Check if user can work with emailActors AND there are emails registered
        this.openEmailDialog();
      } else {
        await this.submitCreateOrder();
      }
    }
  }

  private async submitCreateOrder(emailAddresses: SimplifiedEmailActor[] = []) {
    const isSuccess = await this.sandbox.submitCreateOrder(this.loadingDates, emailAddresses);
    if (isSuccess) {
      const editOrderDetail = await getFirst$$(this.store.select(state => state.editOrderDetail));
      if (!editOrderDetail.recurrenceDates || editOrderDetail.recurrenceDates.length === 1) {
        await this.openCreateFeedbackDialog({
          isSuccess: editOrderDetail.mode.saveSuccess,
          etmOrderNumber: editOrderDetail.orderDetail.etmOrderNumber,
          salesOrderNumber: editOrderDetail.orderDetail.salesOrderNumber,
          incoterm: editOrderDetail.orderDetail.deliveryMethod.incoterm && editOrderDetail.orderDetail.deliveryMethod.incoterm.id,
          otherEtmOrderNumbers: editOrderDetail.orderDetail.singleRecurrenceEtmOrderNumbers,
        });
      }
    }
  }

  backToDeliveryClicked() {
    this.store.dispatch(new orderActions.OrderDetailGoBackToDeliveryAction('order'));
  }

  backToDetailClicked() {
    this.store.dispatch(new orderActions.OrderDetailGoBackToDetailAction('order'));
  }

  saveDetails() {
    this.store.dispatch(new orderActions.OrderDetailSaveOrderDetailAction({}));
  }

  savePlanning(form) {
    this.store.dispatch(new orderActions.OrderDetailSavePlanningAction(form));
    this.scrollService.scrollToElement('submit-button', 200);
  }

  getAvailablePickingDates(): string[] {
    return this.orderDetail.fullTruck ? this.availablePickingDates.full : this.availablePickingDates.nonFull;
  }

  getOpeningHours(): CDOpeningHours[] {
    const orderType = this.contractInfo.orderTypes && this.contractInfo.orderTypes.find(orderType => orderType.id === this.orderDetail.deliveryMethod.type);
    const shippingCondition =
      orderType &&
      this.orderDetail.deliveryMethod &&
      this.orderDetail.deliveryMethod.shippingCondition &&
      orderType.shippingConditions.find(sc => sc.id === this.orderDetail.deliveryMethod.shippingCondition.id);
    const from = shippingCondition && this.orderDetail.deliveryMethod.from && shippingCondition.froms.find(from => from.id === this.orderDetail.deliveryMethod.from.id);
    const to = from && this.orderDetail.deliveryMethod.to && from.tos.find(to => to.id === this.orderDetail.deliveryMethod.to.id);
    const transportByEps = this.orderDetail.deliveryMethod && this.orderDetail.deliveryMethod.shippingCondition && this.orderDetail.deliveryMethod.shippingCondition.id === 'Z1';
    const isDelivery = this.orderDetail.deliveryMethod.type === 'EPS-CUS';

    const depot = isDelivery ? from : to;
    const location = isDelivery ? to : from;

    if (transportByEps) {
      return location && location.openingHours;
    } else {
      // Transport by customer
      return depot && depot.openingHours;
    }
  }

  createCCR(): void {
    this.store.dispatch(
      goAction({
        path: [`/ccr/${this.orderDetail.etmOrderNumber}`],
        query: { salesOrderNumber: this.orderDetail.salesOrderNumber },
      })
    );
  }

  canCreateCCR(): boolean {
    return false;
    // TODO add this.orderDetail.canCreateCCR but it is not yet available on orderDetail
    // return (this.isSuperUser || this.restrictions.useCcrValidation) && !this.orderDetail.ccr.ccrNumber;
  }

  copyOrder(): void {
    this.sandbox.copyOrder(this.orderDetail.etmOrderNumber);
  }

  get disableButtons(): boolean {
    return (this.templateState && this.templateState.saving) || (this.mode && (this.mode.saving || this.mode.state === 'recurrence-dates'));
  }

  downloadDocument(etmOrderNumber: string, extension: string): void {
    throw new Error('Not possible to download THE order document.  Not a feature that is supported.  Only for flows');
  }

  downloadDocumentFromList({ documentId, extension }): void {
    this.store.dispatch(new orderActions.OrderDetailDocumentDownloadAction({ documentId, extension }));
  }

  openEmailDialog() {
    const modalData = { emailAddresses: this.emailAddresses, loadingDates: this.loadingDates };
    const config: MatDialogConfig = {
      width: '50%',
      height: 'auto',
      data: modalData,
      role: 'dialog',
    };
    this.nextOnEnterPressed$.next(false);
    this.dialog
      .open(SelectEmailAddressesModalComponent, config)
      .afterClosed()
      .subscribe(async result => {
        this.nextOnEnterPressed$.next(true);

        const selectedEmailAddresses: SimplifiedEmailActor[] = this.emailAddresses
          .filter(actor => {
            if (result.emailAddresses && actor.id === result.emailAddresses.find(emailAddress => emailAddress === actor.id)) {
              return actor;
            }
          })
          .map((emailActor: EmailActorsItem) => {
            return {
              email: emailActor.email,
              name: emailActor.name,
            };
          });
        if (result && result.isSubmit) {
          await this.submitCreateOrder(selectedEmailAddresses);
        } else {
          this.store.dispatch(new orderActions.OrderDetailBackToCreateConfirmAction());
          this.loadingDates = null;
          this.recurrenceService.closeRecurrenceDatesDialog();
        }
      });

    return of(modalData);
  }

  protected openCreateFeedbackDialog({
    isSuccess,
    etmOrderNumber,
    salesOrderNumber,
    incoterm,
    otherEtmOrderNumbers = [{ etmOrderNumber: '' }]
  }): Promise<void> {
    return super.openCreateFeedbackDialog({
      isSuccess,
      etmOrderNumber,
      salesOrderNumber,
      incoterm,
      ccrNumber: null,
      messageType: '',
      showDownloadDocumentButton: false,
      showCreateButton: true,
      moduleRoute: 'orders',
      createAction: new orderActions.OrderDetailCreateStartAction({
        selectedCustomer: this.orderDetail.deliveryMethod.soldTo,
        date: null,
        restrictions: this.restrictions,
        preventCheckingContractDetails: false,
        isSuperUser: this.isSuperUser,
      }),
      showCopyButton: true,
      copyFunction: this.sandbox.copyOrder.bind(this.sandbox),
      otherEtmOrderNumbers,
    });
  }

  loadContractInfoFromOrderTypeToIncoterm(orderTypeId: string) {
    this.sandbox.loadContractInfoFromOrderTypeToIncoterm(orderTypeId);
  }

  loadContractInfoFromOrderTypeToFrom(form) {
    // this.sandbox.loadContractInfoFromFromToIncoterm(this.orderDetail.deliveryMethod.soldTo.id, 'ORDER', this.form);
  }

  resetCreatePage() {
    const isTemplate = this.mode.createTemplate || this.mode.editTemplate;
    this.sandbox.resetCreatePage(isTemplate);
  }

  showEmptyContractModal() {
    const isTemplate = this.mode.createTemplate || this.mode.editTemplate;
    this.sandbox.showEmptyContractModal(isTemplate);
  }

  isCustomerAllowedToCreateOrUpdate(customerId: string): boolean {
    return !!this.customers.find(c => c.id === customerId);
  }

  showCopyButton(): boolean {
    return this.isSuperUser ? this.isCustomerAllowedToCreateOrUpdate(this.orderDetail.customer.id) : this.authorization.ORDER.CREATE;
  }
}
